import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Bell,
  BellRing,
  Check,
  CheckCheck,
  X,
  AlertTriangle,
  Clock,
  FileText,
  Shield,
  UserCheck,
  Settings,
  ChevronDown,
  Filter,
  Trash2,
  ChevronRight,
  Search,
  Keyboard,
} from 'lucide-react';
import NotificationEngine, {
  NOTIFICATION_TYPE,
} from '../../services/notifications/NotificationEngine';
import { useAppContext } from '../../context/AppContext';
import { EmptyState, Tooltip } from '../Common/ReusableUI';
import { useToast } from '../Common/ToastProvider';

// ── Severity visual mapping ──────────────────────────────────────────────────

const SEVERITY_STYLES = {
  info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: Clock, badge: 'bg-blue-100 text-blue-700' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: AlertTriangle, badge: 'bg-amber-100 text-amber-700' },
  critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: AlertTriangle, badge: 'bg-red-100 text-red-700' },
  success: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: Check, badge: 'bg-emerald-100 text-emerald-700' },
};

const TYPE_ICONS = {
  [NOTIFICATION_TYPE.ACER_DEADLINE]: Shield,
  [NOTIFICATION_TYPE.ASSESSMENT_OVERDUE]: FileText,
  [NOTIFICATION_TYPE.CARE_PLAN_REVIEW]: FileText,
  [NOTIFICATION_TYPE.CREDENTIAL_EXPIRY]: UserCheck,
  [NOTIFICATION_TYPE.TASK_OVERDUE]: Clock,
  [NOTIFICATION_TYPE.TASK_ESCALATED]: AlertTriangle,
  [NOTIFICATION_TYPE.DOCUMENT_PENDING]: FileText,
  [NOTIFICATION_TYPE.SETTLING_TRANSITION]: Check,
  [NOTIFICATION_TYPE.WORKFLOW_COMPLETE]: CheckCheck,
  [NOTIFICATION_TYPE.SYSTEM]: Bell,
};

// ── Storage keys & pagination ────────────────────────────────────────────────

const PREFS_KEY = 'mf_notification_prefs';
const SNOOZE_KEY = 'mf_notification_snooze';
const PAGE_SIZE = 20;

// ── Notification categories for preferences panel ────────────────────────────

const CATEGORIES = {
  clinical: {
    label: 'Clinical',
    description: 'Assessments, care plans, settling-in',
    types: [
      NOTIFICATION_TYPE.ASSESSMENT_OVERDUE,
      NOTIFICATION_TYPE.CARE_PLAN_REVIEW,
      NOTIFICATION_TYPE.SETTLING_TRANSITION,
    ],
  },
  medication: {
    label: 'Medication',
    description: 'Medication alerts & reminders',
    types: [],
  },
  administrative: {
    label: 'Administrative',
    description: 'Tasks, credentials, documents',
    types: [
      NOTIFICATION_TYPE.TASK_OVERDUE,
      NOTIFICATION_TYPE.TASK_ESCALATED,
      NOTIFICATION_TYPE.CREDENTIAL_EXPIRY,
      NOTIFICATION_TYPE.DOCUMENT_PENDING,
      NOTIFICATION_TYPE.WORKFLOW_COMPLETE,
      NOTIFICATION_TYPE.SYSTEM,
    ],
  },
  compliance: {
    label: 'Compliance',
    description: 'ACER deadlines & regulatory audits',
    types: [NOTIFICATION_TYPE.ACER_DEADLINE],
  },
};

const SNOOZE_OPTIONS = [
  { label: '1 hour', hours: 1 },
  { label: '4 hours', hours: 4 },
  { label: 'Tomorrow', hours: 24 },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function getActionForType(notification) {
  const { type, actionUrl, linkedModule } = notification;

  if (linkedModule) {
    const labelMap = {
      assessments: 'View Assessment',
      medication: 'Go to Medication',
      residents: 'Open Resident',
      documents: 'View Document',
      tasks: 'View Task',
      credentials: 'View Credentials',
      workflows: 'View Workflow',
    };
    return { label: labelMap[linkedModule] || 'View Details', module: linkedModule };
  }

  switch (type) {
    case NOTIFICATION_TYPE.ASSESSMENT_OVERDUE:
    case NOTIFICATION_TYPE.CARE_PLAN_REVIEW:
    case NOTIFICATION_TYPE.ACER_DEADLINE:
      return { label: 'View Assessment', module: actionUrl || 'assessments' };
    case NOTIFICATION_TYPE.SETTLING_TRANSITION:
      return { label: 'Open Resident', module: actionUrl || 'residents' };
    case NOTIFICATION_TYPE.CREDENTIAL_EXPIRY:
      return { label: 'View Credentials', module: actionUrl || 'credentials' };
    case NOTIFICATION_TYPE.DOCUMENT_PENDING:
      return { label: 'View Document', module: actionUrl || 'documents' };
    case NOTIFICATION_TYPE.TASK_OVERDUE:
    case NOTIFICATION_TYPE.TASK_ESCALATED:
      return { label: 'View Task', module: actionUrl || 'tasks' };
    case NOTIFICATION_TYPE.WORKFLOW_COMPLETE:
      return { label: 'View Workflow', module: actionUrl || 'workflows' };
    default:
      return { label: 'View Details', module: actionUrl || null };
  }
}

function matchesSearch(notification, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    notification.title?.toLowerCase().includes(q) ||
    notification.message?.toLowerCase().includes(q) ||
    notification.type?.toLowerCase().includes(q)
  );
}

function isTypeEnabled(type, prefs) {
  for (const [key, cat] of Object.entries(CATEGORIES)) {
    if (cat.types.includes(type)) return prefs[key] !== false;
  }
  return true;
}

function groupByDate(items) {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(todayStart.getDate() - 1);
  const weekStart = new Date(todayStart);
  weekStart.setDate(todayStart.getDate() - 7);

  const buckets = { today: [], yesterday: [], thisWeek: [], older: [] };

  for (const n of items) {
    const d = new Date(n.createdAt);
    if (d >= todayStart) buckets.today.push(n);
    else if (d >= yesterdayStart) buckets.yesterday.push(n);
    else if (d >= weekStart) buckets.thisWeek.push(n);
    else buckets.older.push(n);
  }

  return [
    { key: 'today', label: 'Today', items: buckets.today },
    { key: 'yesterday', label: 'Yesterday', items: buckets.yesterday },
    { key: 'thisWeek', label: 'This Week', items: buckets.thisWeek },
    { key: 'older', label: 'Older', items: buckets.older },
  ].filter((g) => g.items.length > 0);
}

function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    const defaults = { clinical: true, medication: true, administrative: true, compliance: true };
    return raw ? { ...defaults, ...JSON.parse(raw) } : defaults;
  } catch {
    return { clinical: true, medication: true, administrative: true, compliance: true };
  }
}

function persistPrefs(prefs) {
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
}

function loadSnoozed() {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function persistSnoozed(map) {
  localStorage.setItem(SNOOZE_KEY, JSON.stringify(map));
}

function isSnoozed(id, snoozedMap) {
  const until = snoozedMap[id];
  return until ? new Date(until) > new Date() : false;
}

// ── Preferences Panel ────────────────────────────────────────────────────────

function PreferencesPanel({ prefs, onChange, onClose }) {
  const { showToast } = useToast();
  const [local, setLocal] = useState(prefs);

  const toggle = (key) => setLocal((p) => ({ ...p, [key]: !p[key] }));

  const save = () => {
    persistPrefs(local);
    onChange(local);
    showToast('Notification preferences saved', 'success');
    onClose();
  };

  return (
    <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 animate-[fadeIn_0.15s_ease-out]">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Notification Preferences</p>
        <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-200">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="space-y-1.5">
        {Object.entries(CATEGORIES).map(([key, cat]) => (
          <button
            key={key}
            type="button"
            onClick={() => toggle(key)}
            className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-white transition-colors"
          >
            <div>
              <span className="text-xs font-semibold text-slate-800">{cat.label}</span>
              <span className="block text-[10px] text-slate-500">{cat.description}</span>
            </div>
            <div
              className={`relative h-5 w-9 rounded-full transition-colors ${local[key] ? 'bg-purple-500' : 'bg-slate-300'}`}
            >
              <span
                className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${local[key] ? 'translate-x-4' : 'translate-x-0.5'}`}
              />
            </div>
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={save}
        className="mt-2 w-full rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-purple-700 transition-colors"
      >
        Save Preferences
      </button>
    </div>
  );
}

// ── Snooze Dropdown ──────────────────────────────────────────────────────────

function SnoozeDropdown({ onSnooze, onCloseMenu }) {
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onCloseMenu();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onCloseMenu]);

  return (
    <div
      ref={menuRef}
      className="absolute right-0 top-full z-10 mt-1 min-w-[120px] rounded-lg border border-slate-200 bg-white py-1 shadow-xl animate-[fadeIn_0.1s_ease-out]"
    >
      {SNOOZE_OPTIONS.map((opt) => (
        <button
          key={opt.hours}
          type="button"
          onClick={() => onSnooze(opt.hours)}
          className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
        >
          <Clock className="h-3 w-3" />
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Single Notification Row ──────────────────────────────────────────────────

function NotificationItem({
  notification,
  isFocused,
  onMarkRead,
  onDismiss,
  onSnooze,
  onAction,
  onKeyDown,
  itemRef,
}) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const n = notification;
  const style = SEVERITY_STYLES[n.severity] || SEVERITY_STYLES.info;
  const TypeIcon = TYPE_ICONS[n.type] || Bell;
  const action = getActionForType(n);

  const handleSnooze = (hours) => {
    onSnooze(n.id, hours);
    setSnoozeOpen(false);
  };

  return (
    <div
      ref={itemRef}
      tabIndex={0}
      role="listitem"
      onKeyDown={onKeyDown}
      className={`group relative border-b border-slate-100 px-4 py-3 transition-colors hover:bg-slate-50 outline-none
        ${!n.read ? 'bg-purple-50/30' : ''}
        ${isFocused ? 'ring-2 ring-inset ring-purple-400 bg-purple-50/20' : ''}
      `}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${style.bg} ${style.border} border`}>
          <TypeIcon className={`h-4 w-4 ${style.text}`} />
        </div>

        {/* Body */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className={`text-sm font-semibold leading-tight ${!n.read ? 'text-slate-900' : 'text-slate-700'}`}>
              {n.title}
            </p>
            <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${style.badge}`}>
              {n.severity}
            </span>
          </div>

          <p className="mt-0.5 text-xs text-slate-600 line-clamp-2">{n.message}</p>

          {/* Meta row */}
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] text-slate-400">{timeAgo(n.createdAt)}</span>

            {!n.read && (
              <button type="button" onClick={() => onMarkRead(n.id)} className="text-[10px] font-medium text-purple-600 hover:text-purple-800">
                Mark read
              </button>
            )}

            {/* Deep-link action button */}
            <button
              type="button"
              onClick={() => onAction(n, action)}
              className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-purple-100 hover:text-purple-700 transition-colors"
            >
              {action.label}
              <ChevronRight className="h-2.5 w-2.5" />
            </button>
          </div>
        </div>

        {/* Right-side actions */}
        <div className="flex shrink-0 flex-col items-center gap-1">
          {/* Snooze */}
          <div className="relative">
            <Tooltip content="Snooze" position="left">
              <button
                type="button"
                onClick={() => setSnoozeOpen(!snoozeOpen)}
                className="rounded-md p-1 text-slate-300 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-amber-100 hover:text-amber-600"
                aria-label="Snooze notification"
              >
                <Clock className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
            {snoozeOpen && (
              <SnoozeDropdown
                onSnooze={handleSnooze}
                onCloseMenu={() => setSnoozeOpen(false)}
              />
            )}
          </div>

          {/* Dismiss */}
          <button
            type="button"
            onClick={() => onDismiss(n.id)}
            className="shrink-0 rounded-md p-1 text-slate-300 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-slate-200 hover:text-slate-600"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Unread dot */}
      {!n.read && (
        <span className="absolute left-1.5 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-purple-500" />
      )}
    </div>
  );
}

// ── Notification Bell (header icon) ──────────────────────────────────────────

export function NotificationBell({ onClick }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    setCount(NotificationEngine.getUnreadCount('*'));
    const unsub = NotificationEngine.subscribe(() => {
      setCount(NotificationEngine.getUnreadCount('*'));
    });
    const interval = setInterval(() => {
      setCount(NotificationEngine.getUnreadCount('*'));
    }, 15000);
    return () => { unsub(); clearInterval(interval); };
  }, []);

  const hasUnread = count > 0;
  const Icon = hasUnread ? BellRing : Bell;

  return (
    <Tooltip content={hasUnread ? `${count} unread notification${count !== 1 ? 's' : ''}` : 'Notifications'}>
      <button
        type="button"
        onClick={onClick}
        className="relative inline-flex items-center justify-center rounded-lg p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
        aria-label={`Notifications${hasUnread ? ` (${count} unread)` : ''}`}
      >
        <Icon className={`h-5 w-5 ${hasUnread ? 'text-purple-600 animate-pulse' : ''}`} />
        {hasUnread && (
          <span className="absolute -top-0.5 -right-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white shadow-sm">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>
    </Tooltip>
  );
}

// ── Main Notification Center Panel ───────────────────────────────────────────

export default function NotificationCenter({ onClose }) {
  const { state } = useAppContext();
  const { selectedFacility } = state;
  const { showToast } = useToast();

  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [prefs, setPrefs] = useState(loadPrefs);
  const [snoozedMap, setSnoozedMap] = useState(loadSnoozed);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [, setTick] = useState(0);

  const panelRef = useRef(null);
  const listRef = useRef(null);
  const itemRefs = useRef([]);
  const searchRef = useRef(null);

  // ── Data loading & subscription ──

  const refresh = useCallback(() => {
    const all = NotificationEngine.getAll({
      facilityId: selectedFacility !== 'all' ? selectedFacility : undefined,
    });
    setNotifications(all);
  }, [selectedFacility]);

  useEffect(() => {
    refresh();
    const unsub = NotificationEngine.subscribe(() => refresh());
    return unsub;
  }, [refresh]);

  // Periodically purge expired snoozes
  useEffect(() => {
    const interval = setInterval(() => {
      setSnoozedMap((prev) => {
        const now = new Date();
        const cleaned = {};
        let changed = false;
        for (const [id, until] of Object.entries(prev)) {
          if (new Date(until) > now) cleaned[id] = until;
          else changed = true;
        }
        if (changed) persistSnoozed(cleaned);
        return changed ? cleaned : prev;
      });
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Refresh relative timestamps every 60s
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(timer);
  }, []);

  // ── Click-outside & Escape ──

  useEffect(() => {
    function handleClickOutside(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    function handleEscape(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // ── Filtering pipeline ──

  const processedList = useMemo(() => {
    let list = notifications;

    if (filter === 'unread') list = list.filter((n) => !n.read);
    else if (filter !== 'all') list = list.filter((n) => n.type === filter);

    list = list.filter((n) => isTypeEnabled(n.type, prefs));
    list = list.filter((n) => !isSnoozed(n.id, snoozedMap));

    if (searchQuery) list = list.filter((n) => matchesSearch(n, searchQuery));

    return list;
  }, [notifications, filter, prefs, snoozedMap, searchQuery]);

  const paginatedList = useMemo(() => processedList.slice(0, visibleCount), [processedList, visibleCount]);
  const hasMore = processedList.length > visibleCount;
  const dateGroups = useMemo(() => groupByDate(paginatedList), [paginatedList]);

  // ── Derived counts ──

  const unreadCount = notifications.filter((n) => !n.read).length;
  const readCount = notifications.filter((n) => n.read).length;

  // ── Handlers ──

  const handleMarkRead = useCallback((id) => {
    NotificationEngine.markRead(id);
    refresh();
  }, [refresh]);

  const handleMarkAllRead = useCallback(() => {
    NotificationEngine.markAllRead('*');
    refresh();
    showToast('All notifications marked as read', 'success');
  }, [refresh, showToast]);

  const handleDismiss = useCallback((id) => {
    NotificationEngine.dismiss(id);
    refresh();
  }, [refresh]);

  const handleDismissAllRead = useCallback(() => {
    const readIds = notifications.filter((n) => n.read).map((n) => n.id);
    readIds.forEach((id) => NotificationEngine.dismiss(id));
    refresh();
    showToast(`Dismissed ${readIds.length} read notification${readIds.length !== 1 ? 's' : ''}`, 'info');
  }, [notifications, refresh, showToast]);

  const handleSnooze = useCallback((id, hours) => {
    const until = new Date(Date.now() + hours * 3600000).toISOString();
    setSnoozedMap((prev) => {
      const next = { ...prev, [id]: until };
      persistSnoozed(next);
      return next;
    });
    const label = SNOOZE_OPTIONS.find((o) => o.hours === hours)?.label || `${hours}h`;
    showToast(`Snoozed for ${label}`, 'info');
  }, [showToast]);

  const handleAction = useCallback((notification, action) => {
    if (!notification.read) {
      NotificationEngine.markRead(notification.id);
      refresh();
    }
    const target = notification.linkedModule || action.module || notification.type;
    showToast(`Navigating to ${target}...`, 'info');
  }, [refresh, showToast]);

  const handleLoadMore = () => setVisibleCount((c) => c + PAGE_SIZE);

  // ── Keyboard navigation ──

  const flatItemCount = paginatedList.length;

  const handleListKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((i) => {
          const next = Math.min(i + 1, flatItemCount - 1);
          const el = itemRefs.current[next];
          el?.focus();
          el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((i) => {
          const next = Math.max(i - 1, 0);
          const el = itemRefs.current[next];
          el?.focus();
          el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          return next;
        });
      } else if (e.key === 'Enter' && focusedIndex >= 0 && focusedIndex < flatItemCount) {
        e.preventDefault();
        const n = paginatedList[focusedIndex];
        if (n) handleAction(n, getActionForType(n));
      } else if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    },
    [flatItemCount, focusedIndex, paginatedList, handleAction]
  );

  // Assign refs array length
  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, flatItemCount);
  }, [flatItemCount]);

  // Track running flat index across groups
  let runningIndex = 0;

  return (
    <div
      ref={panelRef}
      className="fixed right-4 top-16 z-50 w-[420px] max-h-[calc(100vh-80px)] rounded-xl border border-slate-200 bg-white shadow-2xl flex flex-col overflow-hidden"
      role="dialog"
      aria-label="Notification Center"
    >
      {/* ── Header ── */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-purple-50 to-white px-4 py-3">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-purple-600" />
          <h2 className="text-sm font-bold text-slate-900">Notifications</h2>
          {unreadCount > 0 && (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
              {unreadCount} unread
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Tooltip content="Preferences">
            <button
              type="button"
              onClick={() => setShowPrefs((v) => !v)}
              className={`rounded-md p-1.5 transition-colors ${showPrefs ? 'bg-purple-100 text-purple-700' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'}`}
              aria-label="Notification preferences"
            >
              <Settings className="h-4 w-4" />
            </button>
          </Tooltip>

          {unreadCount > 0 && (
            <Tooltip content="Mark all as read">
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Mark all as read"
              >
                <CheckCheck className="h-4 w-4" />
              </button>
            </Tooltip>
          )}

          {readCount > 0 && (
            <Tooltip content="Dismiss all read">
              <button
                type="button"
                onClick={handleDismissAllRead}
                className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Dismiss all read notifications"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </Tooltip>
          )}

          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close notifications"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* ── Preferences Panel (expandable) ── */}
      {showPrefs && (
        <PreferencesPanel
          prefs={prefs}
          onChange={setPrefs}
          onClose={() => setShowPrefs(false)}
        />
      )}

      {/* ── Search & Filter Bar ── */}
      <div className="border-b border-slate-100 px-3 py-2 space-y-2">
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setVisibleCount(PAGE_SIZE); }}
            placeholder='Search notifications… (press "/")'
            className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-8 text-xs text-slate-800 placeholder:text-slate-400 focus:border-purple-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-purple-300 transition-colors"
            aria-label="Search notifications"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-600"
              aria-label="Clear search"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Filter pills */}
        <div className="flex items-center gap-1 overflow-x-auto">
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="flex shrink-0 items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            <Filter className="h-3 w-3" />
            Filter
            <ChevronDown className={`h-3 w-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
          {['all', 'unread', NOTIFICATION_TYPE.ASSESSMENT_OVERDUE, NOTIFICATION_TYPE.ACER_DEADLINE, NOTIFICATION_TYPE.TASK_OVERDUE, NOTIFICATION_TYPE.CREDENTIAL_EXPIRY, NOTIFICATION_TYPE.DOCUMENT_PENDING].map((f) => {
            const count = f === 'all'
              ? notifications.length
              : f === 'unread'
                ? unreadCount
                : notifications.filter((n) => n.type === f).length;
            return (
              <button
                key={f}
                type="button"
                onClick={() => { setFilter(f); setVisibleCount(PAGE_SIZE); }}
                className={`flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  filter === f
                    ? 'bg-purple-100 text-purple-700'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}
              >
                {f === 'all' ? 'All' : f === 'unread' ? 'Unread' : f.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                {count > 0 && (
                  <span className={`inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[9px] font-bold ${
                    filter === f ? 'bg-purple-200 text-purple-800' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Notification List ── */}
      <div
        ref={listRef}
        className="flex-1 overflow-y-auto"
        role="list"
        onKeyDown={handleListKeyDown}
      >
        {processedList.length === 0 ? (
          <div className="py-10">
            <EmptyState
              icon={searchQuery ? Search : Bell}
              title={searchQuery ? 'No results' : 'No notifications'}
              description={
                searchQuery
                  ? `Nothing matched "${searchQuery}". Try different keywords.`
                  : filter !== 'all'
                    ? 'Try a different filter or check your preferences'
                    : "You're all caught up!"
              }
            />
          </div>
        ) : (
          <>
            {dateGroups.map((group) => (
              <div key={group.key}>
                {/* Date group header */}
                <div className="sticky top-0 z-[1] bg-slate-100/90 backdrop-blur-sm px-4 py-1.5 border-b border-slate-200 flex items-center justify-between">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    {group.label}
                  </p>
                  <span className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-slate-200 px-1.5 text-[9px] font-bold text-slate-600">
                    {group.items.length}
                  </span>
                </div>

                {group.items.map((n) => {
                  const idx = runningIndex++;
                  return (
                    <NotificationItem
                      key={n.id}
                      notification={n}
                      isFocused={focusedIndex === idx}
                      onMarkRead={handleMarkRead}
                      onDismiss={handleDismiss}
                      onSnooze={handleSnooze}
                      onAction={handleAction}
                      onKeyDown={handleListKeyDown}
                      itemRef={(el) => { itemRefs.current[idx] = el; }}
                    />
                  );
                })}
              </div>
            ))}

            {/* Load More */}
            {hasMore && (
              <div className="px-4 py-3 text-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-4 py-1.5 text-xs font-semibold text-purple-600 shadow-sm hover:bg-purple-50 hover:border-purple-200 transition-colors"
                >
                  Load more
                  <ChevronDown className="h-3 w-3" />
                </button>
                <p className="mt-1 text-[10px] text-slate-400">
                  Showing {paginatedList.length} of {processedList.length}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="border-t border-slate-200 bg-slate-50 px-4 py-2">
        <div className="flex items-center justify-between">
          <p className="text-[10px] text-slate-500">
            {processedList.length} notification{processedList.length !== 1 ? 's' : ''}
            {Object.keys(snoozedMap).length > 0 && (
              <span className="ml-1 text-amber-600">
                · {Object.values(snoozedMap).filter((u) => new Date(u) > new Date()).length} snoozed
              </span>
            )}
          </p>
          <Tooltip content="↑↓ Navigate · Enter Act · Esc Close · / Search" position="left">
            <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 cursor-default">
              <Keyboard className="h-3 w-3" />
              Shortcuts
            </span>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
